/**
 * Custom user storage to bypass VueUse serialization issues
 * This ensures user data is always properly stored as JSON
 */

import { ref, Ref, watch } from 'vue';
import { User } from '@/types/user';

const STORAGE_KEY = 'user.user';

/**
 * Custom localStorage wrapper for user data
 */
export function createUserStorage(): Ref<User | null> {
  // Initialize the ref with current localStorage value
  const storedValue = localStorage.getItem(STORAGE_KEY);
  let initialValue: User | null = null;

  if (
    storedValue &&
    storedValue !== '[object Object]' &&
    storedValue !== 'null'
  ) {
    try {
      initialValue = JSON.parse(storedValue);
      console.log('✅ Loaded user from localStorage:', initialValue);
    } catch (e) {
      console.error('Failed to parse stored user data:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
  } else if (storedValue === '[object Object]') {
    console.error('🚨 Found corrupted user data, clearing it');
    localStorage.removeItem(STORAGE_KEY);
  }

  // Create reactive ref
  const userRef = ref<User | null>(initialValue);

  // Watch for changes and save to localStorage
  watch(
    userRef,
    (newValue) => {
      if (newValue === null || newValue === undefined) {
        localStorage.setItem(STORAGE_KEY, 'null');
        console.log('📝 Cleared user in localStorage');
      } else {
        try {
          // Ensure we're working with a plain object
          const plainObject = JSON.parse(JSON.stringify(newValue));
          const serialized = JSON.stringify(plainObject);

          // Safety check
          if (serialized === '[object Object]') {
            console.error(
              '🚨 Attempted to save corrupted user data, blocking!'
            );
            return;
          }

          localStorage.setItem(STORAGE_KEY, serialized);
          console.log('📝 Saved user to localStorage:', plainObject);
        } catch (e) {
          console.error('Failed to save user to localStorage:', e);
        }
      }
    },
    { deep: true }
  );

  return userRef;
}
